import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pedagogyService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

const TEMPLATES = [
  { id: 'classic', label: 'Classic', sectionStyle: 'underline' },
  { id: 'modern', label: 'Modern', sectionStyle: 'bar' },
  { id: 'ministerial', label: 'Ministerial', sectionStyle: 'boxed' },
];

const LANGUAGES = [
  { id: 'ar', label: 'العربية', dir: 'rtl' },
  { id: 'fr', label: 'Français', dir: 'ltr' },
  { id: 'en', label: 'English', dir: 'ltr' },
];

const COLOR_PRESETS = ['#1d4ed8', '#16a34a', '#7c3aed', '#dc2626', '#d97706', '#0f766e', '#be185d', '#334155'];

const FORM_LABELS = {
  ar: {
    documentTitle: 'عنوان الوثيقة',
    subject: 'المادة', level: 'المستوى', unit: 'الوحدة التعلمية', duration: 'المدة الزمنية',
    session: 'عنوان الحصة', reference: 'المرجع', lessonCard: 'بطاقة الدرس',
    competencies: 'الكفاءات المستهدفة', terminalCompetency: 'الكفاءة الختامية للميدان',
    targetCompetency: 'الكفاءة المستهدفة / مؤشراتها', teachingAids: 'الوسائل التعليمية',
    sessionFlow: 'سير الحصة', stage: 'المرحلة', activity: 'الأنشطة التعلمية', timing: 'التوقيت',
    evaluation: 'التقويم', school: 'المؤسسة', year: 'السنة الدراسية', teacher: 'الأستاذ(ة)', date: 'التاريخ',
    chooseLesson: 'اختر درسًا لتعبئة هذه الحصة', addStage: 'إضافة مرحلة', addLesson: 'إضافة حصة',
    sessionLabel: 'الحصة',
  },
  fr: {
    documentTitle: 'Titre du document',
    subject: 'Matière', level: 'Niveau', unit: "Unité d'apprentissage", duration: 'Durée',
    session: 'Titre de la séance', reference: 'Référence', lessonCard: 'Fiche du cours',
    competencies: 'Compétences visées', terminalCompetency: 'Compétence terminale du domaine',
    targetCompetency: 'Compétence visée / indicateurs', teachingAids: 'Moyens didactiques',
    sessionFlow: 'Déroulement de la séance', stage: 'Étape', activity: "Activités d'apprentissage", timing: 'Durée',
    evaluation: 'Évaluation', school: 'Établissement', year: 'Année scolaire', teacher: 'Enseignant(e)', date: 'Date',
    chooseLesson: 'Choisir une leçon pour remplir cette séance', addStage: 'Ajouter une étape', addLesson: 'Ajouter une séance',
    sessionLabel: 'Séance',
  },
  en: {
    documentTitle: 'Document Title',
    subject: 'Subject', level: 'Level', unit: 'Learning Unit', duration: 'Duration',
    session: 'Session Title', reference: 'Reference', lessonCard: 'Lesson Card',
    competencies: 'Targeted Competencies', terminalCompetency: 'Terminal Competency of the Domain',
    targetCompetency: 'Targeted Competency / Indicators', teachingAids: 'Teaching Aids',
    sessionFlow: 'Session Flow', stage: 'Stage', activity: 'Learning Activities', timing: 'Timing',
    evaluation: 'Evaluation', school: 'School', year: 'School Year', teacher: 'Teacher', date: 'Date',
    chooseLesson: 'Choose a lesson to fill this session', addStage: 'Add stage', addLesson: 'Add session',
    sessionLabel: 'Session',
  },
};

// The printed PDF's own field labels (backend pedagogy/pdf.py LABELS, minus
// the fixed gov/ministry/title letterhead) - a teacher can rename any of
// these (e.g. "session_label": "Session" -> "Hissa") and have it saved as
// their default for every future note. Keys match pdf.CUSTOMIZABLE_LABEL_KEYS
// exactly, since that's what gets sent back as `custom_labels`.
const PRINT_LABEL_DEFAULTS = {
  ar: {
    school: 'المؤسسة', year: 'السنة الدراسية', teacher: 'الأستاذ(ة)', date: 'التاريخ',
    lesson_card: 'بطاقة الدرس', subject: 'المادة', level: 'المستوى', unit: 'الوحدة التعلمية',
    duration: 'المدة الزمنية', session: 'عنوان الحصة', reference: 'المرجع',
    competencies: 'الكفاءات المستهدفة', terminal_competency: 'الكفاءة الختامية للميدان',
    target_competency: 'الكفاءة المستهدفة / مؤشراتها', teaching_aids: 'الوسائل التعليمية',
    session_flow: 'سير الحصة', timing: 'التوقيت', activities: 'الأنشطة التعلمية', stages: 'المراحل',
    evaluation: 'التقويم', teacher_signature: 'توقيع الأستاذ(ة)', director_visa: 'تأشيرة المدير(ة)',
    session_label: 'الحصة',
  },
  fr: {
    school: 'Établissement', year: 'Année scolaire', teacher: 'Enseignant(e)', date: 'Date',
    lesson_card: 'Fiche du cours', subject: 'Matière', level: 'Niveau', unit: "Unité d'apprentissage",
    duration: 'Durée', session: 'Titre de la séance', reference: 'Référence',
    competencies: 'Compétences visées', terminal_competency: 'Compétence terminale du domaine',
    target_competency: 'Compétence visée / indicateurs', teaching_aids: 'Moyens didactiques',
    session_flow: 'Déroulement de la séance', timing: 'Durée', activities: "Activités d'apprentissage", stages: 'Étapes',
    evaluation: 'Évaluation', teacher_signature: "Signature de l'enseignant(e)", director_visa: 'Visa du directeur/de la directrice',
    session_label: 'Séance',
  },
  en: {
    school: 'School', year: 'School Year', teacher: 'Teacher', date: 'Date',
    lesson_card: 'Lesson Card', subject: 'Subject', level: 'Level', unit: 'Learning Unit',
    duration: 'Duration', session: 'Session Title', reference: 'Reference',
    competencies: 'Targeted Competencies', terminal_competency: 'Terminal Competency of the Domain',
    target_competency: 'Targeted Competency / Indicators', teaching_aids: 'Teaching Aids',
    session_flow: 'Session Flow', timing: 'Timing', activities: 'Learning Activities', stages: 'Stages',
    evaluation: 'Evaluation', teacher_signature: "Teacher's Signature", director_visa: "Principal's Visa",
    session_label: 'Session',
  },
};

// Maps a backend label key (as stored/sent in custom_labels) to the
// corresponding FORM_LABELS key used to render the content tab + preview,
// where one exists - lets a rename show up everywhere it's used, not just
// in the generated PDF.
const LABEL_KEY_TO_FORM_KEY = {
  session_label: 'sessionLabel', lesson_card: 'lessonCard', subject: 'subject', level: 'level',
  unit: 'unit', duration: 'duration', session: 'session', reference: 'reference',
  competencies: 'competencies', terminal_competency: 'terminalCompetency', target_competency: 'targetCompetency',
  teaching_aids: 'teachingAids', session_flow: 'sessionFlow', timing: 'timing', evaluation: 'evaluation',
  school: 'school', year: 'year', teacher: 'teacher', date: 'date',
  stages: 'stage', activities: 'activity',
};

const DEFAULT_STAGES = {
  ar: [
    { stage: 'تمهيد', activity: '', timing: '5 د' },
    { stage: 'بناء التعلمات', activity: '', timing: '25 د' },
    { stage: 'توظيف المكتسبات', activity: '', timing: '10 د' },
    { stage: 'تقويم', activity: '', timing: '5 د' },
  ],
  fr: [
    { stage: 'Mise en situation', activity: '', timing: '5 min' },
    { stage: "Construction de l'apprentissage", activity: '', timing: '25 min' },
    { stage: 'Réinvestissement', activity: '', timing: '10 min' },
    { stage: 'Évaluation', activity: '', timing: '5 min' },
  ],
  en: [
    { stage: 'Introduction', activity: '', timing: '5 min' },
    { stage: 'Building knowledge', activity: '', timing: '25 min' },
    { stage: 'Application', activity: '', timing: '10 min' },
    { stage: 'Assessment', activity: '', timing: '5 min' },
  ],
};

function stripHtml(html) {
  if (!html) return '';
  const withBreaks = String(html).replace(/<\/(p|div|li|br)>/gi, '\n');
  const text = withBreaks.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');
  return text.replace(/[ \t]+/g, ' ').replace(/\n\s*\n+/g, '\n').trim();
}

function currentAcademicYear() {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() >= 8 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
}

function formatDateForDocument(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}-${m}-${y}`;
}

let sessionKeySeq = 0;
function nextKey() {
  sessionKeySeq += 1;
  return `session-${sessionKeySeq}`;
}

function blankSession(language, moduleData) {
  return {
    key: nextKey(),
    subject: moduleData?.name || '',
    level: (moduleData?.grades || []).map((g) => g.name).join('، ') || '',
    unit: moduleData?.name || '',
    duration: '',
    sessionTitle: '',
    reference: '',
    terminalCompetency: moduleData ? stripHtml(moduleData.description).slice(0, 240) : '',
    targetCompetency: '',
    teachingAids: '',
    evaluation: '',
    stages: (DEFAULT_STAGES[language] || DEFAULT_STAGES.ar).map((row) => ({ ...row })),
    selectedLessonId: '',
  };
}

function sessionFromSaved(saved, language) {
  return {
    key: nextKey(),
    subject: saved.subject || '',
    level: saved.level || '',
    unit: saved.unit || '',
    duration: saved.duration || '',
    sessionTitle: saved.session_title || '',
    reference: saved.reference || '',
    terminalCompetency: saved.terminal_competency || '',
    targetCompetency: saved.target_competency || '',
    teachingAids: saved.teaching_aids || '',
    evaluation: saved.evaluation || '',
    stages: (saved.stages && saved.stages.length ? saved.stages : DEFAULT_STAGES[language] || DEFAULT_STAGES.ar).map((row) => ({ ...row })),
    selectedLessonId: '',
  };
}

/** Opens a configurable pedagogical-note generator ("مذكرة بيداغوجية" /
 * "Fiche Pédagogique" / "Pedagogical Note") for one course: Arabic, French
 * or English, three visual templates, a customizable accent color, smart
 * pre-fill from the course's own data, and the ability to cover several of
 * the course's lessons in the same document ("+ Add session") rather than
 * being limited to one. Every generation is saved to the teacher's
 * document folder - pass `existingDocument` to reopen this in edit mode
 * instead of creating a new one. */
export default function PedagogicalNoteModal({ open, onClose, module, currentUser, existingDocument, onSaved }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [tab, setTab] = useState('content');
  const [template, setTemplate] = useState('classic');
  const [language, setLanguage] = useState('ar');
  const [accentColor, setAccentColor] = useState(COLOR_PRESETS[0]);
  const [generating, setGenerating] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [customLabels, setCustomLabels] = useState({});
  const [labelsSaving, setLabelsSaving] = useState(false);

  const [shared, setShared] = useState({
    schoolName: '', schoolYear: currentAcademicYear(), teacherName: '', date: new Date().toISOString().slice(0, 10),
  });
  const [sessions, setSessions] = useState([]);

  const isRtl = (LANGUAGES.find((l) => l.id === language) || LANGUAGES[0]).dir === 'rtl';
  const languageOverrides = customLabels[language] || {};
  const labels = { ...(FORM_LABELS[language] || FORM_LABELS.ar) };
  Object.entries(LABEL_KEY_TO_FORM_KEY).forEach(([printKey, formKey]) => {
    if (languageOverrides[printKey]) labels[formKey] = languageOverrides[printKey];
  });
  const lessonOptions = (module?.categories || []).flatMap(
    (category) => (category.lessons || []).map((lesson) => ({ ...lesson, categoryName: category.name })),
  );

  useEffect(() => {
    if (!open) return;
    const initialLanguage = existingDocument?.language || 'ar';
    setLanguage(initialLanguage);
    setTemplate(existingDocument?.template || 'classic');
    setAccentColor(existingDocument?.accent_color || COLOR_PRESETS[0]);
    setDocumentTitle(existingDocument?.title || module?.name || '');
    setShared({
      schoolName: existingDocument?.school_name || '',
      schoolYear: existingDocument?.school_year || currentAcademicYear(),
      teacherName: existingDocument?.teacher_name || [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' '),
      date: new Date().toISOString().slice(0, 10),
    });
    if (existingDocument?.sessions?.length) {
      setSessions(existingDocument.sessions.map((s) => sessionFromSaved(s, initialLanguage)));
    } else {
      setSessions([blankSession(initialLanguage, module)]);
    }
    setTab('content');
    pedagogyService.getLabelSettings().then((res) => setCustomLabels(res.data?.labels || {})).catch(() => {});
    // Deliberately NOT depending on module?.id: in edit mode the module is
    // fetched asynchronously by the parent page and arrives *after* this
    // effect's first run, so including it here made the effect re-fire the
    // moment it loaded - silently resetting template/language/sessions back
    // to the saved document if the teacher had already changed anything in
    // that window (the "picked Modern, previewed it, but the PDF came out
    // Classic" bug). module is only ever needed for prefill defaults, which
    // only apply on the very first run anyway (existingDocument has no
    // sessions the first time only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingDocument?.id]);

  if (!open) return null;

  const stagesMatchAnyDefault = (stages) => Object.values(DEFAULT_STAGES).some(
    (preset) => preset.length === stages.length && preset.every((row, i) => row.stage === stages[i].stage && !stages[i].activity),
  );

  const handleLanguageChange = (nextLanguage) => {
    setLanguage(nextLanguage);
    // Stage names are the one thing seeded with language-specific default
    // text (تمهيد / Mise en situation / Introduction, ...) rather than
    // real course data - refresh them on a language switch, but only while
    // they're still untouched placeholders, never overwriting anything the
    // teacher actually typed into "activity".
    setSessions((prev) => prev.map((session) => (
      stagesMatchAnyDefault(session.stages)
        ? { ...session, stages: (DEFAULT_STAGES[nextLanguage] || DEFAULT_STAGES.ar).map((row) => ({ ...row })) }
        : session
    )));
  };

  const updateCustomLabel = (printKey, value) => setCustomLabels((prev) => ({
    ...prev,
    [language]: { ...(prev[language] || {}), [printKey]: value },
  }));

  const saveLabelDefaults = async () => {
    setLabelsSaving(true);
    try {
      await pedagogyService.updateLabelSettings({ labels: customLabels });
      addNotification(t('Saved - these names will be used for all your future notes'), 'success');
    } catch (error) {
      addNotification(t('Failed to save your custom field names'), 'error');
    } finally {
      setLabelsSaving(false);
    }
  };

  const updateShared = (key, value) => setShared((prev) => ({ ...prev, [key]: value }));
  const updateSession = (key, patch) => setSessions((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const removeSession = (key) => setSessions((prev) => (prev.length > 1 ? prev.filter((s) => s.key !== key) : prev));
  const addSession = () => setSessions((prev) => [...prev, blankSession(language, null)]);

  const applyLesson = (sessionKey, lessonId) => {
    const session = sessions.find((s) => s.key === sessionKey);
    const lesson = lessonOptions.find((l) => String(l.id) === String(lessonId));
    if (!lesson) {
      updateSession(sessionKey, { selectedLessonId: lessonId });
      return;
    }
    const patch = { selectedLessonId: lessonId, sessionTitle: lesson.title };
    if (lesson.duration_minutes) {
      patch.duration = language === 'ar' ? `${lesson.duration_minutes} دقيقة` : `${lesson.duration_minutes} min`;
    }
    if (!session.targetCompetency && lesson.content) {
      patch.targetCompetency = stripHtml(lesson.content).slice(0, 240);
    }
    updateSession(sessionKey, patch);
  };

  const updateStage = (sessionKey, index, key, value) => {
    setSessions((prev) => prev.map((s) => (s.key !== sessionKey ? s : {
      ...s, stages: s.stages.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    })));
  };
  const addStage = (sessionKey) => setSessions((prev) => prev.map((s) => (s.key !== sessionKey ? s : { ...s, stages: [...s.stages, { stage: '', activity: '', timing: '' }] })));
  const removeStage = (sessionKey, index) => setSessions((prev) => prev.map((s) => (s.key !== sessionKey ? s : { ...s, stages: s.stages.filter((_, i) => i !== index) })));

  const handleGenerate = async () => {
    if (sessions.length === 0) return;
    setGenerating(true);
    try {
      const payload = {
        document_id: existingDocument?.id || undefined,
        module_id: module?.id || existingDocument?.module || undefined,
        title: documentTitle,
        template, language, accent_color: accentColor, custom_labels: languageOverrides,
        school_name: shared.schoolName, school_year: shared.schoolYear,
        teacher_name: shared.teacherName, date: formatDateForDocument(shared.date),
        sessions: sessions.map((s) => ({
          subject: s.subject, level: s.level, unit: s.unit, duration: s.duration,
          session_title: s.sessionTitle, reference: s.reference,
          terminal_competency: s.terminalCompetency, target_competency: s.targetCompetency,
          teaching_aids: s.teachingAids, evaluation: s.evaluation, stages: s.stages,
        })),
      };
      const response = await pedagogyService.generate(payload);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentTitle || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addNotification(t('Document generated and saved to your documents'), 'success');
      onSaved?.();
      onClose();
    } catch (error) {
      addNotification(t('Failed to generate the document'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const activeTheme = TEMPLATES.find((tpl) => tpl.id === template) || TEMPLATES[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-surface-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </span>
            <div>
              <h2 className="text-lg font-display font-bold text-gray-800">
                {existingDocument ? t('Edit Teaching Note') : t('Generate Teaching Note')}
              </h2>
              <p className="text-gray-500 text-sm">{t('Pedagogical document following Algerian Ministry of Education format · A4')}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {/* Left: form */}
          <div className="lg:w-3/5 overflow-y-auto p-6 border-b lg:border-b-0 lg:border-l border-surface-100">
            <div className="flex items-center justify-between mb-5">
              <div className="flex gap-1 rounded-xl bg-gray-100 p-1 text-sm w-fit">
                <button type="button" onClick={() => setTab('content')}
                  className={`rounded-lg px-4 py-1.5 font-semibold transition-colors ${tab === 'content' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  {t('Content')}
                </button>
                <button type="button" onClick={() => setTab('style')}
                  className={`rounded-lg px-4 py-1.5 font-semibold transition-colors ${tab === 'style' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  {t('Style')}
                </button>
                <button type="button" onClick={() => setTab('labels')}
                  className={`rounded-lg px-4 py-1.5 font-semibold transition-colors ${tab === 'labels' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  {t('Field names')}
                </button>
              </div>
              <div className="flex gap-1 rounded-xl bg-gray-100 p-1 text-sm">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => handleLanguageChange(lang.id)}
                    className={`rounded-lg px-3 py-1.5 font-semibold transition-colors ${language === lang.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {tab === 'content' ? (
              <div className="space-y-5" dir={isRtl ? 'rtl' : 'ltr'}>
                <Field label={labels.documentTitle} value={documentTitle} onChange={setDocumentTitle} rtl={isRtl} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label={labels.school} value={shared.schoolName} onChange={(v) => updateShared('schoolName', v)} rtl={isRtl} />
                  <Field label={labels.year} value={shared.schoolYear} onChange={(v) => updateShared('schoolYear', v)} rtl={isRtl} />
                  <Field label={labels.teacher} value={shared.teacherName} onChange={(v) => updateShared('teacherName', v)} rtl={isRtl} />
                  <Field label={labels.date} type="date" value={shared.date} onChange={(v) => updateShared('date', v)} rtl={isRtl} />
                </div>

                <div className="space-y-4">
                  {sessions.map((session, index) => (
                    <div key={session.key} className={sessions.length > 1 ? 'rounded-xl border border-gray-200 p-4' : 'border-t border-gray-100 pt-4'}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-gray-700">
                          {sessions.length > 1 ? `${labels.sessionLabel} ${index + 1}` : labels.lessonCard}
                        </h3>
                        {sessions.length > 1 && (
                          <button type="button" onClick={() => removeSession(session.key)} className="text-gray-300 hover:text-red-500 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {lessonOptions.length > 0 && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1">{labels.chooseLesson}</label>
                          <select
                            value={session.selectedLessonId}
                            onChange={(e) => applyLesson(session.key, e.target.value)}
                            className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'}`}
                          >
                            <option value="">—</option>
                            {lessonOptions.map((lesson) => (
                              <option key={lesson.id} value={lesson.id}>{lesson.categoryName} — {lesson.title}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <Field label={labels.subject} value={session.subject} onChange={(v) => updateSession(session.key, { subject: v })} rtl={isRtl} />
                        <Field label={labels.level} value={session.level} onChange={(v) => updateSession(session.key, { level: v })} rtl={isRtl} />
                        <Field label={labels.unit} value={session.unit} onChange={(v) => updateSession(session.key, { unit: v })} rtl={isRtl} />
                        <Field label={labels.duration} value={session.duration} onChange={(v) => updateSession(session.key, { duration: v })} rtl={isRtl} />
                        <Field label={labels.session} value={session.sessionTitle} onChange={(v) => updateSession(session.key, { sessionTitle: v })} rtl={isRtl} className="col-span-2" />
                        <Field label={labels.reference} value={session.reference} onChange={(v) => updateSession(session.key, { reference: v })} rtl={isRtl} className="col-span-2" />
                      </div>

                      <div className="mt-3 space-y-3">
                        <TextArea label={`${labels.competencies}: ${labels.terminalCompetency}`} value={session.terminalCompetency} onChange={(v) => updateSession(session.key, { terminalCompetency: v })} rtl={isRtl} />
                        <TextArea label={labels.targetCompetency} value={session.targetCompetency} onChange={(v) => updateSession(session.key, { targetCompetency: v })} rtl={isRtl} />
                        <TextArea label={labels.teachingAids} value={session.teachingAids} onChange={(v) => updateSession(session.key, { teachingAids: v })} rtl={isRtl} rows={2} />
                      </div>

                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-gray-500">{labels.sessionFlow}</label>
                          <button type="button" onClick={() => addStage(session.key)} className="text-xs font-semibold text-primary-600 hover:underline">
                            + {labels.addStage}
                          </button>
                        </div>
                        <div className="space-y-2">
                          {session.stages.map((row, i) => (
                            <div key={i} className="flex gap-2 items-start">
                              <input value={row.stage} onChange={(e) => updateStage(session.key, i, 'stage', e.target.value)}
                                placeholder={labels.stage} dir={isRtl ? 'rtl' : 'ltr'}
                                className={`w-28 rounded-lg border border-gray-200 px-2 py-1.5 text-sm ${isRtl ? 'text-right' : 'text-left'}`} />
                              <input value={row.activity} onChange={(e) => updateStage(session.key, i, 'activity', e.target.value)}
                                placeholder={labels.activity} dir={isRtl ? 'rtl' : 'ltr'}
                                className={`flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm ${isRtl ? 'text-right' : 'text-left'}`} />
                              <input value={row.timing} onChange={(e) => updateStage(session.key, i, 'timing', e.target.value)}
                                placeholder={labels.timing} dir={isRtl ? 'rtl' : 'ltr'}
                                className={`w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm ${isRtl ? 'text-right' : 'text-left'}`} />
                              <button type="button" onClick={() => removeStage(session.key, i)} className="text-gray-300 hover:text-red-500 p-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-3">
                        <TextArea label={labels.evaluation} value={session.evaluation} onChange={(v) => updateSession(session.key, { evaluation: v })} rtl={isRtl} rows={2} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <button type="button" onClick={addSession} className="text-sm font-semibold text-primary-600 hover:underline">
                    + {labels.addLesson}
                  </button>
                </div>
              </div>
            ) : tab === 'style' ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3">{t('Template')}</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => setTemplate(tpl.id)}
                        className={`rounded-xl border-2 p-2 transition ${template === tpl.id ? 'border-primary-500' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <TemplateThumbnail sectionStyle={tpl.sectionStyle} color={accentColor} />
                        <p className="mt-2 text-xs font-semibold text-gray-700">{t(tpl.label)}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3">{t('Title / accent color')}</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {COLOR_PRESETS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setAccentColor(color)}
                        style={{ backgroundColor: color }}
                        className={`w-8 h-8 rounded-full transition ring-offset-2 ${accentColor === color ? 'ring-2 ring-gray-400' : ''}`}
                        aria-label={color}
                      />
                    ))}
                    <label className="w-8 h-8 rounded-full border border-dashed border-gray-300 flex items-center justify-center cursor-pointer overflow-hidden relative">
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </label>
                    <span className="text-xs font-mono text-gray-400 ms-1">{accentColor}</span>
                  </div>
                </div>

                <div className="rounded-xl bg-gray-50 p-3 text-xs text-gray-500">
                  {t('Paper format')}: <span className="font-semibold text-gray-700">A4</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4" dir={isRtl ? 'rtl' : 'ltr'}>
                <p className="text-xs text-gray-500">
                  {t('Rename any field label to whatever you use in your own notes (e.g. "Session" → "Hissa"). Changes apply to this document right away; save them as your default to reuse on every future note.')}
                </p>
                <div className="space-y-2">
                  {Object.keys(PRINT_LABEL_DEFAULTS.ar).map((printKey) => {
                    const defaultText = (PRINT_LABEL_DEFAULTS[language] || PRINT_LABEL_DEFAULTS.ar)[printKey];
                    const value = languageOverrides[printKey] || '';
                    return (
                      <div key={printKey} className="flex items-center gap-2">
                        <input
                          value={value}
                          onChange={(e) => updateCustomLabel(printKey, e.target.value)}
                          placeholder={defaultText}
                          dir={isRtl ? 'rtl' : 'ltr'}
                          className={`flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none ${isRtl ? 'text-right' : 'text-left'}`}
                        />
                        {value && (
                          <button type="button" onClick={() => updateCustomLabel(printKey, '')} className="text-gray-300 hover:text-red-500 p-1.5 shrink-0" title={t('Reset to default')}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={saveLabelDefaults}
                  disabled={labelsSaving}
                  className="w-full rounded-xl bg-gray-900 text-white text-sm font-semibold py-2.5 transition disabled:opacity-60"
                >
                  {labelsSaving ? t('Saving...') : t('Save as default for future notes')}
                </button>
              </div>
            )}
          </div>

          {/* Right: live preview */}
          <div className="lg:w-2/5 bg-gray-100 overflow-y-auto p-6 flex flex-col items-center">
            <p className="text-xs font-semibold text-gray-500 mb-3 self-start">
              {t('Preview (approximate)')}{sessions.length > 1 ? ` · ${t('{{count}} lessons in this document', { count: sessions.length })}` : ''}
            </p>
            <DocumentPreview
              shared={shared}
              session={sessions[0]}
              sessionCount={sessions.length}
              color={accentColor}
              sectionStyle={activeTheme.sectionStyle}
              boxedHeader={template === 'ministerial'}
              language={language}
              rtl={isRtl}
              labels={labels}
            />
          </div>
        </div>

        <div className="p-4 border-t border-surface-100 flex items-center justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition">
            {t('Cancel')}
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || sessions.length === 0}
            className="px-6 py-2 rounded-xl text-sm font-semibold text-white transition disabled:opacity-60"
            style={{ backgroundColor: accentColor }}
          >
            {generating ? t('Generating...') : t('Generate PDF')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', className = '', rtl = true }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir={type === 'date' ? 'ltr' : (rtl ? 'rtl' : 'ltr')}
        className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none ${rtl && type !== 'date' ? 'text-right' : 'text-left'}`}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 2, rtl = true }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        dir={rtl ? 'rtl' : 'ltr'}
        className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none resize-none ${rtl ? 'text-right' : 'text-left'}`}
      />
    </div>
  );
}

function TemplateThumbnail({ sectionStyle, color }) {
  return (
    <div className="aspect-[210/297] w-full rounded-md border border-gray-200 bg-white p-2 flex flex-col gap-1.5 overflow-hidden">
      <div className="h-1 w-3/4 bg-gray-300 rounded-full mx-auto" />
      <div className="h-1 w-1/2 bg-gray-200 rounded-full mx-auto mb-1" />
      <div className="h-1.5 w-2/3 rounded-full self-end" style={{ backgroundColor: color }} />
      {sectionStyle === 'bar' ? (
        <div className="h-2 w-full rounded-sm mt-1" style={{ backgroundColor: color }} />
      ) : sectionStyle === 'boxed' ? (
        <div className="h-2 w-full rounded-sm mt-1 border" style={{ borderColor: color }} />
      ) : (
        <div className="h-0.5 w-full mt-1.5" style={{ backgroundColor: color }} />
      )}
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-0.5 w-full bg-gray-100 rounded-full" />
      ))}
      <div className="flex-1 border rounded-sm mt-1" style={{ borderColor: color + '55' }} />
    </div>
  );
}

const PREVIEW_STRINGS = {
  ar: { title: 'مذكرة بيداغوجية', gov: 'الجمهورية الجزائرية الديمقراطية الشعبية', ministry: 'وزارة التربية الوطنية' },
  fr: { title: 'Fiche Pédagogique', gov: 'République Algérienne Démocratique et Populaire', ministry: "Ministère de l'Éducation Nationale" },
  en: { title: 'Pedagogical Note', gov: "People's Democratic Republic of Algeria", ministry: 'Ministry of National Education' },
};

function DocumentPreview({ shared, session, sessionCount, color, sectionStyle, boxedHeader, language, rtl, labels }) {
  const strings = PREVIEW_STRINGS[language] || PREVIEW_STRINGS.ar;
  const stages = session?.stages || [];

  const SectionTitle = ({ children }) => {
    if (sectionStyle === 'bar') {
      return <div className="text-white text-[10px] font-bold px-2 py-1 rounded-sm mt-2 mb-1" style={{ backgroundColor: color }}>{children}</div>;
    }
    if (sectionStyle === 'boxed') {
      return <div className="text-[10px] font-bold px-2 py-1 rounded-sm border mt-2 mb-1" style={{ borderColor: color, color }}>{children}</div>;
    }
    return (
      <div className="mt-2 mb-1">
        <p className="text-[10px] font-bold" style={{ color }}>{children}</p>
        <div className="h-px w-full" style={{ backgroundColor: color }} />
      </div>
    );
  };

  if (!session) {
    return <div className="bg-white shadow-lg w-full max-w-[280px] aspect-[210/297] p-4 flex items-center justify-center text-gray-300 text-xs">—</div>;
  }

  const tableHeaders = rtl ? [labels.timing, labels.activity, labels.stage] : [labels.stage, labels.activity, labels.timing];

  return (
    <div dir={rtl ? 'rtl' : 'ltr'} className="bg-white shadow-lg w-full max-w-[280px] aspect-[210/297] p-4 text-[8px] leading-tight overflow-hidden">
      <div className={boxedHeader ? 'border rounded-sm p-1.5 text-center' : 'text-center'} style={boxedHeader ? { borderColor: color } : {}}>
        <p className="font-bold">{strings.gov}</p>
        <p>{strings.ministry}</p>
      </div>
      <p className="text-center font-bold mt-2 mb-1" style={{ color, fontSize: '13px' }}>{strings.title}</p>
      <div className="h-0.5 w-full mb-2" style={{ backgroundColor: color }} />

      <div className="flex justify-between"><span>{labels.school}: {shared.schoolName || '...'}</span><span>{shared.schoolYear}</span></div>
      <div className="flex justify-between"><span>{labels.teacher}: {shared.teacherName || '...'}</span><span>{shared.date ? shared.date.split('-').reverse().join('-') : ''}</span></div>

      {sessionCount > 1 && (
        <p className="font-bold mt-2" style={{ color }}>{labels.sessionLabel} 1 {sessionCount > 1 ? `(+${sessionCount - 1})` : ''}</p>
      )}
      <SectionTitle>{labels.lessonCard}</SectionTitle>
      <div className="flex justify-between"><span>{labels.level}: {session.level || '...'}</span><span>{labels.subject}: {session.subject || '...'}</span></div>
      <p className="truncate">{labels.unit}: {session.unit || '...'}</p>
      <p className="truncate">{labels.session}: {session.sessionTitle || '...'}</p>

      <SectionTitle>{labels.competencies}</SectionTitle>
      <p className="line-clamp-2">{session.terminalCompetency || '...........................'}</p>

      <SectionTitle>{labels.sessionFlow}</SectionTitle>
      <table className="w-full border-collapse">
        <thead>
          <tr style={{ backgroundColor: color }}>
            {tableHeaders.map((h) => (
              <th key={h} className="text-white font-semibold py-0.5 px-1 border" style={{ borderColor: color }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stages.slice(0, 4).map((row, i) => {
            const cells = [
              <td key="t" className="text-center py-0.5 px-1 border border-gray-200">{row.timing}</td>,
              <td key="a" className="truncate py-0.5 px-1 border border-gray-200 max-w-[80px]">{row.activity || '—'}</td>,
              <td key="s" className="text-center py-0.5 px-1 border border-gray-200 font-semibold" style={{ color }}>{row.stage}</td>,
            ];
            return <tr key={i} className={i % 2 ? 'bg-gray-50' : ''}>{rtl ? cells : [...cells].reverse()}</tr>;
          })}
        </tbody>
      </table>
    </div>
  );
}
